import { useEffect, useMemo, useState } from 'react'

import type { OnlyFansList } from '@api'

import { useLists } from '@/modules/chatting/hooks/use-lists'
import { MultiSelect } from '@/modules/shifts/ui/dialogs/multi-select'

interface Props {
  isOpen: boolean
  title: string
  selectedItems?: OnlyFansList['id'][]
  onToggleSelect: (id: OnlyFansList['id']) => void
  onSelectValues: (ids: OnlyFansList['id'][]) => void
  disabledItems?: OnlyFansList['id'][]
}

export const IncludableLists = ({
  isOpen,
  title,
  selectedItems,
  onToggleSelect,
  onSelectValues,
  disabledItems,
}: Props) => {
  const [cachedLists, setCachedLists] = useState<OnlyFansList[]>([])

  const { onLoadMore, isFetchingNextPage, isFetching, lists, setQuery, query } =
    useLists('all-lists', {
      enabled: isOpen,
    })

  useEffect(() => {
    if (lists.length > 0) {
      setCachedLists((prevCached) => {
        const existingIds = new Set(prevCached.map((list) => list.id))

        const uniqueNewLists = lists.filter((list) => !existingIds.has(list.id))

        return [...prevCached, ...uniqueNewLists]
      })
    }
  }, [lists])

  const displayLists = useMemo(() => {
    const allAvailableLists = [...cachedLists, ...lists]

    const uniqueLists = allAvailableLists.reduce((acc, list) => {
      if (!acc.some((existing) => existing.id === list.id)) {
        acc.push(list)
      }

      return acc
    }, [] as OnlyFansList[])

    if (query && query.trim()) {
      const lowerQuery = query.toLowerCase()

      return uniqueLists.filter((list) =>
        list.name?.toLowerCase().includes(lowerQuery),
      )
    }

    return uniqueLists
  }, [lists, cachedLists, query])

  const options = useMemo(
    () =>
      displayLists.map((item) => ({
        value: String(item.id),
        label: item.name,
        disabled: disabledItems?.includes(item.id),
      })),
    [displayLists, disabledItems],
  )

  return (
    <div className="space-y-3">
      <div className="flex justify-between">
        <p className="text-secondary-300 font-medium">{title}</p>
      </div>
      <MultiSelect
        options={options}
        value={selectedItems || []}
        onChange={onSelectValues}
        loadMore={onLoadMore}
        onClose={() => setQuery('')}
        isLoadingMore={isFetchingNextPage || isFetching}
        onSearch={setQuery}
        entity="Lists"
        placeholder="Select a list"
        disableAllSelect
        className="w-full px-2"
        popoverClassName="w-[var(--radix-popover-trigger-width)]"
      />
    </div>
  )
}
